/**
 * THE SWARM MOTHER — the Stage 1 encounter.
 * Spec: docs/BOSS-SWARM-MOTHER.md.
 *
 * Owns the body, the core, the formation arc and the fire. The body itself
 * (SwarmBody) knows nothing about the fight; it is a mass of drones that can
 * be shaped and cut. This is the thing that decides what shape, when to shoot,
 * and when it dies.
 *
 * THE LAW THIS FILE ENFORCES:
 *
 *   1. NO HEALTH BAR DRIVES THE KILL. The Mother dies when her core dies, and
 *      the core cannot be touched until the player has physically cut a
 *      corridor to it. Body integrity is information, never the win condition.
 *   2. ATTACK AND VULNERABILITY ARE THE SAME ACT. The iris is armoured shut.
 *      It opens ONLY to birth a wave — so the moment she hurts you is the only
 *      moment you can hurt her. A weak point on a timer is a metronome; a weak
 *      point that is also the attack is a fight.
 *   3. HER DRONES ARE HER GUNS. Every emitter is a real member at a real
 *      position, so cutting the left flank silences the left flank. The player
 *      feels progress instead of reading it.
 */

import * as THREE from 'three'
import { SwarmBody } from './SwarmBody'
import { FORMATION_ARC, type FormationName } from './SwarmFormations'
import type { BulletSystem } from './BulletSystem'

export type MotherEvent =
  | 'reveal'
  | 'morph'
  | 'iris-open'
  | 'iris-shut'
  | 'core-hit'
  | 'phase'
  | 'death'

export const MOTHER = {
  /** The core. Small on purpose — it must be aimed at, not splashed. */
  coreHp: 900,
  coreRadius: 1.15,
  /** Seconds between birth cycles. The iris opens on this beat. */
  birthPeriod: 9,
  /** How long the iris stays open, and therefore how long the core is live. */
  irisOpen: 1.5,
  /** Multiplier while the iris is open AND the player has line of sight. */
  exposedMultiplier: 3,
  /** Seconds between ordinary volleys. */
  fireCadence: 1.15,
  /** Emitters per volley. Scales with how much body is left. */
  emitters: 26,
} as const

const BODY_Y = 11

export class SwarmMotherSystem {
  readonly group = new THREE.Group()
  readonly body = new SwarmBody()

  onEvent: ((kind: MotherEvent, x: number, y: number) => void) | null = null
  /** Fired when a volley leaves the body, so the caller can voice it. */
  onFire: ((kind: 'curtain' | 'lash', x: number, y: number) => void) | null = null

  active = false
  defeated = false
  /**
   * Suppress the encounter and drive the body only.
   *
   * The body makes standalone guarantees — holes persist, losses are
   * permanent, damage survives a morph — and those are verified in isolation
   * by scripts/swarm-gate.mjs. Once the encounter is live it reshapes and
   * heals the body continuously, which is correct in a fight and makes those
   * guarantees unmeasurable. This lets the gate hold the body still without
   * the gate having to reach inside it.
   *
   * Dev-only. Never set in play.
   */
  inert = false

  private coreHp = MOTHER.coreHp
  private irisTimer = 0
  private irisIsOpen = false
  private fireTimer = 0
  private arcIndex = 0
  private time = 0

  private readonly core: THREE.Mesh
  private readonly iris: THREE.Mesh
  private readonly emitterBuf: Array<{ x: number; y: number }> = []

  constructor() {
    this.group.name = 'SwarmMother'
    this.group.add(this.body.group)

    // The core reads as the only NON-drone thing in the whole boss: smooth,
    // lit from within, and obviously not part of the mass around it.
    this.core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(MOTHER.coreRadius, 2),
      new THREE.MeshStandardMaterial({
        color: '#0a0d10',
        emissive: '#7fe6ff',
        emissiveIntensity: 1.6,
        roughness: 0.2,
        metalness: 0.4,
      }),
    )
    this.core.position.set(0, BODY_Y, 0.5)
    this.group.add(this.core)

    // The iris is armour over the core. While it is shut the core is sealed;
    // it scales away when the Mother opens to birth a wave.
    this.iris = new THREE.Mesh(
      new THREE.IcosahedronGeometry(MOTHER.coreRadius * 1.45, 1),
      new THREE.MeshStandardMaterial({
        color: '#161a1f',
        roughness: 0.45,
        metalness: 0.9,
        flatShading: true,
      }),
    )
    this.iris.position.copy(this.core.position)
    this.group.add(this.iris)

    this.group.visible = false
  }

  get integrity(): number {
    return this.body.integrity
  }

  get coreFraction(): number {
    return Math.max(0, this.coreHp / MOTHER.coreHp)
  }

  /** True only while the iris is open — the window the core can be hurt in. */
  get exposed(): boolean {
    return this.irisIsOpen
  }

  get position(): { x: number; y: number } {
    return { x: 0, y: BODY_Y }
  }

  spawn(inert = false): void {
    this.inert = inert
    this.active = true
    this.defeated = false
    this.coreHp = MOTHER.coreHp
    this.irisTimer = 0
    this.irisIsOpen = false
    this.fireTimer = 0
    this.arcIndex = 0
    this.time = 0
    this.body.reset()
    this.group.visible = true
    this.onEvent?.('reveal', 0, BODY_Y)
  }

  update(dt: number, playerX: number, playerY: number, bullets: BulletSystem): void {
    if (!this.active || this.defeated) return
    this.time += dt
    this.body.update(dt)
    if (this.inert) return

    this.advanceArc()
    this.driveIris(dt)
    this.driveFire(dt, playerX, playerY, bullets)

    // The core pulses harder as it is worn down, so its state is legible from
    // the model rather than only from the HUD.
    const mat = this.core.material as THREE.MeshStandardMaterial
    const urgency = 1 - this.coreFraction
    const pulse = 0.55 + Math.sin(this.time * (3 + urgency * 7)) * 0.45
    mat.emissiveIntensity = this.irisIsOpen ? 1.6 + pulse * 2.6 : 0.5 + pulse * 0.3
  }

  /**
   * Walk the formation arc as the body is cut down.
   *
   * The arc is the Mother's only narrative — raw mass, natural forms, mimicry
   * of the player, her true geometry, collapse — so it is driven by damage
   * rather than by a timer. A player who cuts fast sees her run through her
   * whole repertoire; a cautious one gets more time in each shape.
   */
  private advanceArc(): void {
    const want = Math.min(
      FORMATION_ARC.length - 1,
      Math.floor((1 - this.body.integrity) * FORMATION_ARC.length),
    )
    if (want === this.arcIndex) return
    this.arcIndex = want
    const next: FormationName = FORMATION_ARC[want]
    this.body.setFormation(next)
    this.onEvent?.('morph', 0, BODY_Y)
    this.onEvent?.('phase', 0, BODY_Y)
  }

  /**
   * The iris: armour that opens to birth a wave.
   *
   * This is the whole weak-point design. The Mother cannot push drones out
   * through a shut iris, so the instant she attacks is the instant her core is
   * reachable — provided the player has cut a corridor to see it.
   */
  private driveIris(dt: number): void {
    this.irisTimer += dt
    if (!this.irisIsOpen && this.irisTimer >= MOTHER.birthPeriod) {
      this.irisIsOpen = true
      this.irisTimer = 0
      this.onEvent?.('iris-open', 0, BODY_Y)
    } else if (this.irisIsOpen && this.irisTimer >= MOTHER.irisOpen) {
      this.irisIsOpen = false
      this.irisTimer = 0
      this.onEvent?.('iris-shut', 0, BODY_Y)
    }
    // Armour scales away rather than blinking, so the tell has a duration the
    // player can react inside.
    const target = this.irisIsOpen ? 0.05 : 1
    this.iris.scale.lerp(new THREE.Vector3(target, target, target), Math.min(1, dt * 7))
    this.iris.rotation.z += dt * (this.irisIsOpen ? 3.4 : 0.6)
  }

  /**
   * Fire, sourced from live drones.
   *
   * Emitter count scales with surviving body, so a Mother who has been cut in
   * half literally shoots half as much. That coupling is the reason the fight
   * feels like it is being won rather than merely lasting.
   */
  private driveFire(
    dt: number,
    playerX: number,
    playerY: number,
    bullets: BulletSystem,
  ): void {
    this.fireTimer -= dt
    if (this.fireTimer > 0) return
    this.fireTimer = MOTHER.fireCadence

    const want = Math.max(4, Math.round(MOTHER.emitters * this.body.integrity))
    const n = this.body.sampleLiving(want, this.emitterBuf)
    if (n === 0) return

    // CURTAIN: most emitters drop straight down. Static, readable, and the
    // gaps in it are exactly the gaps the player cut.
    let curtainShots = 0
    for (let i = 0; i < n; i += 1) {
      const e = this.emitterBuf[i]
      // Only members on the lower edge of the mass have line of fire; firing
      // from the middle would rain bullets out of drones that are behind
      // other drones.
      if (e.y > BODY_Y + 1.5) continue
      bullets.spawn(e.x, e.y - 0.4, 0, -9.5, 0.4, 1)
      curtainShots += 1
    }
    // ONE cue for the whole curtain, not one per emitter. Twenty-odd
    // simultaneous plays would clip into mush and drown every other sound in
    // the mix; the bank's curtain sample is already a thousand small sources.
    if (curtainShots > 0) this.onFire?.('curtain', 0, BODY_Y)

    // LASH: a few aimed bolts, sequenced rather than simultaneous, so the
    // player can outrun the sweep instead of being bracketed.
    const lash = Math.min(3, Math.max(1, Math.round(n / 8)))
    for (let i = 0; i < lash; i += 1) {
      const e = this.emitterBuf[(i * 7) % n]
      const dx = playerX - e.x
      const dy = playerY - e.y
      const d = Math.hypot(dx, dy) || 1
      bullets.spawn(e.x, e.y, (dx / d) * 15, (dy / d) * 15, 0.32, 1)
    }
    if (lash > 0) this.onFire?.('lash', this.emitterBuf[0].x, this.emitterBuf[0].y)
  }

  /**
   * Player fire against the boss.
   *
   * Rounds always chew the body. They only reach the core when the iris is
   * open AND the body has actually been cut open along the firing line — the
   * corridor has to be real, which is what SwarmBody.clearLineTo proves.
   */
  damage(sx: number, sy: number, amount: number): {
    dronesKilled: number
    coreHit: boolean
    killed: boolean
  } {
    const dronesKilled = this.body.damage(sx, sy, 0.5, 1)

    let coreHit = false
    let killed = false
    if (this.irisIsOpen && dronesKilled === 0) {
      const dx = sx - 0
      const dy = sy - BODY_Y
      if (dx * dx + dy * dy <= MOTHER.coreRadius * MOTHER.coreRadius) {
        if (this.body.clearLineTo(sx, sy - 6, 0, BODY_Y)) {
          this.coreHp -= amount * MOTHER.exposedMultiplier
          coreHit = true
          this.onEvent?.('core-hit', 0, BODY_Y)
          if (this.coreHp <= 0) {
            this.defeated = true
            killed = true
            this.onEvent?.('death', 0, BODY_Y)
          }
        }
      }
    }
    return { dronesKilled, coreHit, killed }
  }

  /** Contact with the mass, or with the core once it is bare. */
  hits(px: number, py: number, radius: number): boolean {
    return this.body.hits(px, py, radius)
  }

  reset(): void {
    this.active = false
    this.defeated = false
    this.group.visible = false
    this.body.reset()
    this.coreHp = MOTHER.coreHp
    this.irisIsOpen = false
    this.iris.scale.setScalar(1)
  }

  dispose(): void {
    this.body.dispose()
    this.core.geometry.dispose()
    ;(this.core.material as THREE.Material).dispose()
    this.iris.geometry.dispose()
    ;(this.iris.material as THREE.Material).dispose()
  }
}
